import { apiFetch, type ApiResponse } from './api'

export type KBCollection = {
  id: string
  workspaceId: string
  title: string
  slug: string
  parentId: string | null
  order: number
  createdAt: string
  updatedAt: string
}

export type ArticleStatus = 'draft' | 'published'

export type KBArticle = {
  id: string
  workspaceId: string
  collectionId: string
  title: string
  slug: string
  content: Record<string, unknown>
  status: ArticleStatus
  authorId: string
  createdAt: string
  updatedAt: string
}

function workspaceQuery(workspaceId: string) {
  return `workspaceId=${encodeURIComponent(workspaceId)}`
}

export async function getCollections(workspaceId: string) {
  return apiFetch<KBCollection[]>(`/api/knowledge/collections?${workspaceQuery(workspaceId)}`)
}

export async function createCollection(
  workspaceId: string,
  payload: { title: string; parentId?: string | null },
) {
  return apiFetch<KBCollection>(`/api/knowledge/collections?${workspaceQuery(workspaceId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateCollection(
  workspaceId: string,
  id: string,
  payload: { title?: string; parentId?: string | null; order?: number },
) {
  return apiFetch<KBCollection>(
    `/api/knowledge/collections/${id}?${workspaceQuery(workspaceId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  )
}

export async function reorderCollections(
  workspaceId: string,
  items: Array<{ id: string; order: number }>,
) {
  return apiFetch<KBCollection[]>(
    `/api/knowledge/collections/reorder?${workspaceQuery(workspaceId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ items }),
    },
  )
}

export async function deleteCollection(workspaceId: string, id: string) {
  return apiFetch<{ deleted: boolean }>(
    `/api/knowledge/collections/${id}?${workspaceQuery(workspaceId)}`,
    { method: 'DELETE' },
  )
}

export async function getArticles(workspaceId: string, collectionId?: string) {
  const params = new URLSearchParams({ workspaceId })
  if (collectionId) params.set('collectionId', collectionId)
  return apiFetch<KBArticle[]>(`/api/knowledge/articles?${params.toString()}`)
}

export async function getArticle(workspaceId: string, id: string) {
  return apiFetch<KBArticle>(`/api/knowledge/articles/${id}?${workspaceQuery(workspaceId)}`)
}

export async function createArticle(
  workspaceId: string,
  payload: { collectionId: string; title?: string },
) {
  return apiFetch<KBArticle>(`/api/knowledge/articles?${workspaceQuery(workspaceId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateArticle(
  workspaceId: string,
  id: string,
  payload: { title?: string; content?: Record<string, unknown>; collectionId?: string },
) {
  return apiFetch<KBArticle>(`/api/knowledge/articles/${id}?${workspaceQuery(workspaceId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function deleteArticle(workspaceId: string, id: string) {
  return apiFetch<{ deleted: boolean }>(
    `/api/knowledge/articles/${id}?${workspaceQuery(workspaceId)}`,
    { method: 'DELETE' },
  )
}

export async function publishArticle(workspaceId: string, id: string) {
  return apiFetch<KBArticle>(
    `/api/knowledge/articles/${id}/publish?${workspaceQuery(workspaceId)}`,
    { method: 'POST' },
  )
}

export async function unpublishArticle(workspaceId: string, id: string) {
  return apiFetch<KBArticle>(
    `/api/knowledge/articles/${id}/unpublish?${workspaceQuery(workspaceId)}`,
    { method: 'POST' },
  )
}

export async function uploadArticleImage(
  workspaceId: string,
  articleId: string,
  file: File,
): Promise<ApiResponse<{ url: string }>> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(
    `/api/knowledge/articles/${articleId}/images?${workspaceQuery(workspaceId)}`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    },
  )

  return response.json() as Promise<ApiResponse<{ url: string }>>
}

export type TrashArticle = {
  id: string
  workspaceId: string
  collectionId: string
  title: string
  slug: string
  status: ArticleStatus
  deletedAt: string
  createdAt: string
  updatedAt: string
}

export type TrashCollection = {
  id: string
  workspaceId: string
  title: string
  slug: string
  parentId: string | null
  deletedAt: string
  createdAt: string
  updatedAt: string
}

export type TrashData = {
  articles: TrashArticle[]
  collections: TrashCollection[]
}

export async function getTrash(workspaceId: string) {
  return apiFetch<TrashData>(`/api/knowledge/trash?${workspaceQuery(workspaceId)}`)
}

export async function recoverArticle(workspaceId: string, id: string) {
  return apiFetch<TrashArticle>(
    `/api/knowledge/trash/articles/${id}/recover?${workspaceQuery(workspaceId)}`,
    { method: 'POST' },
  )
}

export async function recoverCollection(workspaceId: string, id: string) {
  return apiFetch<TrashCollection>(
    `/api/knowledge/trash/collections/${id}/recover?${workspaceQuery(workspaceId)}`,
    { method: 'POST' },
  )
}
